import { __awaiter } from "tslib";
import * as React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetTravellersQuery, useDefaultTravellerFieldsQuery, useDeleteTravellerMutation, useLocationsQuery } from '@websky/graphql';
import { TravellerTypes } from '../../Traveller/types';
import { useTheme } from '../../theme';
import { useCreateTraveller, useSubmitPersonalDetails, useUpdateTraveller } from '../hooks';
import Traveller from '../../Traveller';
import Loader from '../../Loader';
import { useIsTablet } from '../../utils';
export var renderType;
(function (renderType) {
    renderType[renderType["Passengers"] = 0] = "Passengers";
    renderType[renderType["Companions"] = 1] = "Companions";
})(renderType || (renderType = {}));
const TravelCompanions = ({ format }) => {
    var _a, _b;
    const { t } = useTranslation('Account');
    const { TravelCompanions: theme } = useTheme('TravelCompanions');
    const { Account: accountTheme } = useTheme('AccountModule');
    const isTablet = useIsTablet();
    const [expanded, setExpanded] = useState(false);
    const [deletingTravellerId, setDeletingTravellerId] = useState(null);
    const { data: travellerFieldsData, loading: travellerFieldsLoading } = useDefaultTravellerFieldsQuery();
    const { data: companionsData, refetch: refetchCompanions, loading: companionsLoading } = useGetTravellersQuery();
    const { data: locationsData, loading: locationsLoading } = useLocationsQuery({
        variables: {
            parameters: {
                query: ''
            }
        }
    });
    const cities = React.useMemo(() => { var _a; return (_a = locationsData === null || locationsData === void 0 ? void 0 : locationsData.LocationSuggestions) === null || _a === void 0 ? void 0 : _a.reduce((cities, location) => [...cities, ...location.cities], []); }, [locationsData]);
    const { submit: submitPersonalDetails } = useSubmitPersonalDetails((_a = companionsData === null || companionsData === void 0 ? void 0 : companionsData.CurrentUser) === null || _a === void 0 ? void 0 : _a.userProfile);
    const { submit: createNewCompanion } = useCreateTraveller();
    const { submit: updateTravellerSubmit } = useUpdateTraveller();
    const [deleteTravellerProfile] = useDeleteTravellerMutation();
    const refetchCompanionsAfterRequest = (request) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const result = yield request();
            if (result) {
                yield refetchCompanions();
            }
            return result;
        }
        catch (_c) {
            return false;
        }
    });
    const onSubmitPersonalDetails = (values) => __awaiter(void 0, void 0, void 0, function* () {
        return refetchCompanionsAfterRequest(() => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield submitPersonalDetails(values);
            return data === null || data === void 0 ? void 0 : data.UpdateUserProfile;
        }));
    });
    const onCreateNewCompanion = (values) => __awaiter(void 0, void 0, void 0, function* () {
        return refetchCompanionsAfterRequest(() => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield createNewCompanion(values);
            const result = data === null || data === void 0 ? void 0 : data.CreateTravellerProfile;
            if (result) {
                setTimeout(() => {
                    setExpanded(false);
                }, 1000);
            }
            return result;
        }));
    });
    const onUpdateTraveller = (id) => (values) => __awaiter(void 0, void 0, void 0, function* () {
        return refetchCompanionsAfterRequest(() => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield updateTravellerSubmit(values, id);
            return data === null || data === void 0 ? void 0 : data.UpdateTravellerProfile;
        }));
    });
    const onDeleteTraveller = (id) => __awaiter(void 0, void 0, void 0, function* () {
        setExpanded(false);
        setDeletingTravellerId(id);
        try {
            return yield refetchCompanionsAfterRequest(() => __awaiter(void 0, void 0, void 0, function* () {
                const { data } = yield deleteTravellerProfile({ variables: { id } });
                return data === null || data === void 0 ? void 0 : data.DeleteTravellerProfile;
            }));
        }
        finally {
            setDeletingTravellerId(null);
        }
    });
    const onChangeExpended = useCallback((index) => (isExpended) => {
        setExpanded(isExpended ? index : false);
    }, []);
    const expendedControlledProps = useCallback((index) => ({
        expanded: expanded === index,
        handleChange: onChangeExpended(index)
    }), [expanded]);
    const isLoading = companionsLoading || travellerFieldsLoading || locationsLoading;
    if (isLoading) {
        return React.createElement(Loader, { className: theme.loader });
    }
    return (React.createElement(React.Fragment, null, ((_b = companionsData === null || companionsData === void 0 ? void 0 : companionsData.CurrentUser) === null || _b === void 0 ? void 0 : _b.travellerProfiles) && (travellerFieldsData === null || travellerFieldsData === void 0 ? void 0 : travellerFieldsData.DefaultTravellerProfile) && (React.createElement("div", { className: theme.companions },
        format === renderType.Passengers && (React.createElement(Traveller, Object.assign({ type: TravellerTypes.PersonalDetails, traveller: Object.assign(Object.assign({}, companionsData.CurrentUser.userProfile), { canBeDeleted: false }), cities: cities, onSubmit: onSubmitPersonalDetails }, expendedControlledProps(0)))),
        format === renderType.Companions && (React.createElement(React.Fragment, null,
            !isTablet && React.createElement("h1", { className: accountTheme.route__title }, t('Travel companions')),
            React.createElement("div", { className: theme.description },
                React.createElement("p", null, t('Usually traveling with a friend or family?')),
                React.createElement("p", null, t('Add them to your companions list and make checking-in altogether easier.'))),
            React.createElement("div", { className: theme.companions__added },
                React.createElement(Traveller, Object.assign({ type: TravellerTypes.CompanionEmpty, traveller: travellerFieldsData.DefaultTravellerProfile, onSubmit: onCreateNewCompanion }, expendedControlledProps(0))),
                companionsData.CurrentUser.travellerProfiles.map((traveller, index) => (React.createElement(Traveller, Object.assign({ key: traveller.id, type: TravellerTypes.CompanionFilled, traveller: traveller, onSubmit: onUpdateTraveller(traveller.id), onDelete: onDeleteTraveller, disabled: deletingTravellerId === traveller.id }, expendedControlledProps(index + 1))))))))))));
};
export default TravelCompanions;
